import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  LocalOffer,
  Spa,
  LocalFireDepartment,
  Grass,
} from "@mui/icons-material";
import ImageLoader from "../../../../components/ImageLoader/ImageLoader";

const formatLabel = (value) => {
  if (!value || value === "all") return null;
  return value
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const MenuItemCard = ({ item, onAddToCart, subcategoryNameById = {} }) => {
  const theme = useTheme();

  const categoryLabel = formatLabel(item.categoryName);
  const subcategoryLabel = formatLabel(
    item.subcategoryName || subcategoryNameById[item.subcategoryId],
  );
  const hasDescription = Boolean(item.description?.trim());
  const showTaxonomyPills = categoryLabel || subcategoryLabel;

  const pillBaseSx = {
    height: 34,
    fontWeight: 800,
    fontSize: "0.875rem",
    letterSpacing: 0.25,
    borderRadius: 1.75,
    "& .MuiChip-label": { px: 1.25 },
  };

  return (
    <Card
      elevation={0}
      sx={{
        cursor: "pointer",
        height: "auto",
        alignSelf: "flex-start",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        border: item.isOffer
          ? `2px solid ${alpha(theme.palette.error.main, 0.3)}`
          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: "all 0.2s ease",
        position: "relative",
        overflow: "visible",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 20px ${alpha(item.isOffer ? theme.palette.error.main : theme.palette.primary.main, 0.15)}`,
        },
      }}
      onClick={() => onAddToCart(item)}
    >
      {item.isOffer && (
        <Chip
          icon={<LocalOffer sx={{ fontSize: 16 }} />}
          label={item.discount}
          size="small"
          sx={{
            position: "absolute",
            top: -12,
            right: 8,
            bgcolor: theme.palette.error.main,
            color: "white",
            fontWeight: 700,
            fontSize: "0.75rem",
            height: 24,
            zIndex: 1,
            "& .MuiChip-icon": {
              color: "white",
            },
          }}
        />
      )}

      <CardContent
        sx={{
          flex: hasDescription ? 1 : "none",
          display: "flex",
          flexDirection: "column",
          pt: 1.5,
          pb: hasDescription ? 2 : 1.25,
          "&:last-child": { pb: hasDescription ? 2 : 1.25 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: 120,
            bgcolor: item.isOffer
              ? alpha(theme.palette.error.main, 0.05)
              : alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.5,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {item.image ? (
            <ImageLoader
              imagePath={item.image}
              width="100%"
              height={120}
              alt={item.name}
              borderRadius={2}
            />
          ) : (
            <Typography
              variant="h3"
              sx={{
                opacity: 0.2,
                fontWeight: 700,
              }}
            >
              {item.name.charAt(0)}
            </Typography>
          )}

          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              display: "flex",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            {item.isVegetarian && (
              <Chip
                icon={<Grass sx={{ fontSize: 14 }} />}
                label="Veg"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.9),
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 20,
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
            )}
            {item.isVegan && (
              <Chip
                icon={<Spa sx={{ fontSize: 14 }} />}
                label="Vegan"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.success.dark, 0.9),
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 20,
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
            )}
            {item.isSpicy && (
              <Chip
                icon={<LocalFireDepartment sx={{ fontSize: 14 }} />}
                label="Spicy"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.9),
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 20,
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
            )}
          </Box>
        </Box>

        {showTaxonomyPills && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 1,
            }}
          >
            {categoryLabel && (
              <Chip
                label={categoryLabel}
                sx={{
                  ...pillBaseSx,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.dark,
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                }}
              />
            )}
            {subcategoryLabel && (
              <Chip
                label={subcategoryLabel}
                sx={{
                  ...pillBaseSx,
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.dark,
                  border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                }}
              />
            )}
          </Box>
        )}

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: hasDescription ? 0.5 : 0.75,
            fontSize: "1rem",
            lineHeight: 1.25,
          }}
        >
          {item.name}
        </Typography>

        {hasDescription && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1.5,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {item.description}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: hasDescription ? 1.5 : 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: item.isOffer
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
                fontSize: "1.25rem",
              }}
            >
              Rs.{" "}
              {item.variations?.length > 0
                ? [...item.variations].sort(
                    (a, b) => a.displayOrder - b.displayOrder,
                  )[0].price
                : item.price}
            </Typography>
            {item.isOffer && item.originalPrice && (
              <Typography
                variant="body2"
                sx={{
                  textDecoration: "line-through",
                  color: "text.disabled",
                  fontSize: "0.875rem",
                }}
              >
                Rs. {item.originalPrice}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MenuItemCard;
